'use client'

import Link from 'next/link'
import { useTranslations } from 'next-intl'
import {
  Eye,
  Target,
  ChevronRight,
  Briefcase,
  Users,
  TrendingUp,
  ShoppingCart,
  BarChart3,
} from 'lucide-react'
import type { AboutPageData } from '@/services/booking-context'

interface AboutPageContentProps {
  data: AboutPageData
  tenantName: string
  /**
   * Slots for content that belongs inside this page's flow rather than after
   * it. The estate stats read as part of the introduction, and the hotel cards
   * belong before the closing call to action - appending both after the CTA
   * left the page asking for a booking and then carrying on.
   */
  afterIntro?: React.ReactNode
  beforeCta?: React.ReactNode
}

const SERVICE_ICONS: Record<string, typeof Briefcase> = {
  'Operations Management': Briefcase,
  'Sales & Marketing': TrendingUp,
  'Sales and Marketing': TrendingUp,
  'Revenue Management': BarChart3,
  'Human Resources': Users,
  Procurement: ShoppingCart,
  'Financial Reporting': BarChart3,
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}

export function AboutPageContent({
  data,
  tenantName,
  afterIntro,
  beforeCta,
}: AboutPageContentProps) {
  const t = useTranslations('booking.about')
  return (
    <div className="min-h-[60vh]">
      {/* Hero — compact with message integrated */}
      <section className="relative bg-gradient-to-br from-primary/5 via-white to-accent/5 dark:from-primary/10 dark:via-gray-900 dark:to-accent/10 border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
            {t('aboutTenant', { tenantName })}
          </h1>
          {data.subtitle && (
            <p className="text-base sm:text-lg text-primary/80 font-medium mt-1.5">
              {data.subtitle}
            </p>
          )}
          {data.message && (
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mt-4 max-w-3xl">
              {data.message}
            </p>
          )}
        </div>
      </section>

      {afterIntro}

      {/* Vision / Mission */}
      {(data.vision || data.mission) && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.vision && (
              <div className="group bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 sm:p-8 hover:shadow-lg hover:border-primary/20 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <Eye className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  {t('ourVision')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {data.vision}
                </p>
              </div>
            )}

            {data.mission && (
              <div className="group bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 sm:p-8 hover:shadow-lg hover:border-primary/20 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <Target className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  {t('ourMission')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {data.mission}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Hotel Tiers */}
      {data.tiers && data.tiers.length > 0 && (
        <section className="bg-gray-50 dark:bg-gray-900/50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                {t('ourHotelTiers')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                {t('hotelTiersDescription')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {data.tiers.map((tier, index) => (
                <div
                  key={index}
                  className="group relative bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  {/* Colored accent bar */}
                  <div
                    className="h-1.5"
                    style={{ backgroundColor: tier.color }}
                  />

                  <div className="p-6 sm:p-7">
                    {/* Tier logo */}
                    <div className="flex items-center gap-4 mb-5">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={tier.logo}
                        alt={tier.name}
                        className="h-14 w-auto object-contain"
                      />
                    </div>

                    {/* Target */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-5 leading-relaxed">
                      {tier.target}
                    </p>

                    {/* Features */}
                    <ul className="space-y-2.5">
                      {tier.features.map((feature, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2.5 text-sm text-gray-700 dark:text-gray-300"
                        >
                          <ChevronRight
                            className="h-4 w-4 shrink-0 mt-0.5"
                            style={{ color: tier.color }}
                          />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services */}
      {data.services && data.services.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              {t('ourServices')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('servicesDescription')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.services.map((service, index) => {
              const Icon = SERVICE_ICONS[service] || Briefcase
              return (
                <div
                  key={index}
                  className="group flex items-center gap-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 hover:shadow-md hover:border-primary/20 transition-all duration-300"
                >
                  <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {service}
                  </span>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Founders */}
      {data.founders && data.founders.length > 0 && (
        <section className="bg-gray-50 dark:bg-gray-900/50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                {t('leadership')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                {t('leadershipDescription')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {data.founders.map((founder, index) => (
                <div
                  key={index}
                  className="group bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all duration-300 text-center"
                >
                  <div className="relative pt-8 px-6">
                    <div className="w-28 h-28 mx-auto rounded-full overflow-hidden ring-4 ring-gray-100 dark:ring-gray-700 group-hover:ring-primary/20 transition-all">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={founder.photo}
                        alt={founder.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  <div className="p-5">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg">
                      {founder.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {founder.title}
                    </p>

                    {(founder.linkedin || founder.twitter) && (
                      <div className="flex items-center justify-center gap-2 mt-4">
                        {founder.linkedin && (
                          <a
                            href={founder.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-8 h-8 rounded-lg bg-gray-50 dark:bg-gray-700/50 flex items-center justify-center text-gray-400 hover:text-[#0A66C2] hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                            aria-label={`${founder.name} on LinkedIn`}
                          >
                            <LinkedInIcon className="h-4 w-4" />
                          </a>
                        )}
                        {founder.twitter && (
                          <a
                            href={founder.twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-8 h-8 rounded-lg bg-gray-50 dark:bg-gray-700/50 flex items-center justify-center text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                            aria-label={`${founder.name} on X`}
                          >
                            <XIcon className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Partners */}
      {data.partners && data.partners.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              {t('ourPartners')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('partnersDescription')}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {data.partners.map((partner, index) => (
              <div
                key={index}
                className="group flex items-center justify-center p-5 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 hover:shadow-md hover:border-primary/20 transition-all duration-300"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="h-10 w-auto object-contain grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300"
                  title={partner.name}
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {beforeCta}

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          {t('ctaTitle')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-xl mx-auto">
          {t('ctaDescription')}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            href="/#search"
            className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors"
          >
            {t('bookNow')}
          </Link>
          <Link
            href="/contact"
            className="inline-flex items-center px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            {t('contactUs')}
          </Link>
        </div>
      </section>
    </div>
  )
}
